import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client.js';

const EMPTY = {
  nome: '',
  email: '',
  telefone: '',
  whatsapp_country_code: '+55',
  endereco: '',
  senha: '',
};

const MIN_PASSWORD = 6;

/** Mapeia o cliente recebido para o estado inicial do formulário. */
function customerToForm(customer) {
  if (!customer) return { ...EMPTY };
  return {
    nome: String(customer.nome || ''),
    email: String(customer.email || ''),
    telefone: String(customer.telefone || ''),
    whatsapp_country_code: String(customer.whatsapp_country_code || '+55'),
    endereco: String(customer.endereco || ''),
    senha: '',
  };
}

/**
 * Lógica de estado e persistência do formulário de edição de cliente.
 * @param {{ tenantId: number|string, customer: object|null, onSaved: (c: object) => void }} params
 */
export function useCustomerEditForm({ tenantId, customer, onSaved }) {
  const [form, setForm] = useState(() => customerToForm(customer));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(customerToForm(customer));
    setError('');
  }, [customer]);

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validate = useCallback(() => {
    if (!form.nome.trim()) return 'O nome é obrigatório.';
    const senha = form.senha.trim();
    if (senha && senha.length < MIN_PASSWORD) {
      return `A senha precisa ter pelo menos ${MIN_PASSWORD} caracteres.`;
    }
    return '';
  }, [form]);

  const submit = useCallback(async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return false;
    }
    setError('');
    setBusy(true);
    try {
      const body = {
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefone: form.telefone.trim(),
        whatsapp_country_code: form.whatsapp_country_code.trim(),
        endereco: form.endereco.trim(),
      };
      if (form.senha.trim()) body.senha = form.senha.trim();
      const res = await api(`/tenants/${tenantId}/customers/${customer.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      onSaved(res?.customer || { ...customer, ...body });
      return true;
    } catch (e) {
      setError(e?.message || 'Não foi possível salvar as alterações.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [form, validate, tenantId, customer, onSaved]);

  return { form, setField, busy, error, submit };
}
